import axios from "axios";
import { BASE_API_URL } from "../consts";

export type ReservationStatus =
  | "pending"
  | "confirmed"
  | "seated"
  | "completed"
  | "paid"
  | "waitlisted"
  | "cancelled"
  | "no-show";

export type PaymentStatus = "unpaid" | "paid" | "partially_paid";

export type ReservationSource = "internal" | "website" | "widget" | "phone" | "voice";

export interface RestaurantTable {
  _id: string;
  name: string;
  capacity: number;
  isActive: boolean;
}

export interface GuestReview {
  rating: number;
  comment?: string | null;
  waiterName: string;
  createdAt: string;
}

export interface Reservation {
  _id: string;
  clientId: string;
  guestName: string;
  guestPhone: string;
  guestEmail?: string | null;
  date: string;
  time: string;
  numberOfGuests: number;
  note?: string | null;
  status: ReservationStatus;
  source: ReservationSource;
  tableId?: string | null;
  tableName?: string | null;
  tableIds?: string[] | null;
  tableNames?: string[] | null;
  waiterName?: string | null;
  createdBy?: { _id: string; firstName: string; lastName: string } | null;
  confirmedBy?: { _id: string; firstName: string; lastName: string } | null;
  confirmedAt?: string | null;
  cancellationReason?: string | null;
  guestReview?: GuestReview | null;
  eventId?: string | { _id: string; title: string } | null;
  eventTableNumber?: number | null;
  ticketsCount?: number;
  paymentStatus?: PaymentStatus;
  amountPaid?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ReservationStats {
  date: string;
  total: number;
  confirmed: number;
  pending: number;
  seated: number;
  noShow: number;
  cancelled: number;
  totalGuests: number;
}

export interface ReservationSettings {
  restaurantName: string;
  tables: RestaurantTable[];
  openingTime: string;
  closingTime: string;
  slotDurationMinutes: number;
  maxGuestsPerSlot: number;
  maxGuestsPerReservation: number;
  requireConfirmation: boolean;
  maxAdvanceBookingDays: number;
  minNoticeHours: number;
  blockedDates: string[];
  notificationEmail?: string | null;
}

export interface AvailabilitySlot {
  time: string;
  available: boolean;
  remaining: number;
  isPast: boolean;
}

export interface Availability {
  available: boolean;
  reason?: string;
  date?: string;
  openingTime?: string;
  closingTime?: string;
  restaurantName?: string;
  slots: AvailabilitySlot[];
}

export interface GetReservationsParams {
  from?: string;
  to?: string;
  status?: ReservationStatus | "all";
  source?: ReservationSource;
  search?: string;
  page?: number;
  limit?: number;
}

// ─── Internal endpoints ───────────────────────────────────────────────────────

export const getReservations = (clientId: string, params: GetReservationsParams = {}) =>
  axios.get<{ reservations: Reservation[]; total: number }>(
    `${BASE_API_URL}/${clientId}/reservations`,
    { params }
  );

export const getReservationById = (id: string) =>
  axios.get<Reservation>(`${BASE_API_URL}/reservations/${id}`);

export const getReservationStats = (clientId: string, date?: string) =>
  axios.get<ReservationStats>(`${BASE_API_URL}/${clientId}/reservations/stats`, {
    params: date ? { date } : {},
  });

export const createReservation = (
  clientId: string,
  data: {
    guestName: string;
    guestPhone: string;
    guestEmail?: string;
    date: string;
    time: string;
    numberOfGuests: number;
    note?: string;
    source?: ReservationSource;
    status?: ReservationStatus;
    tableId?: string;
    tableName?: string;
    tableIds?: string[];
    tableNames?: string[];
    waiterName?: string;
  }
) => axios.post(`${BASE_API_URL}/${clientId}/reservations`, data);

export const updateReservation = (id: string, data: Partial<Reservation>) =>
  axios.put(`${BASE_API_URL}/reservations/${id}`, data);

export const updateReservationStatus = (
  id: string,
  status: ReservationStatus,
  cancellationReason?: string
) =>
  axios.patch(`${BASE_API_URL}/reservations/${id}/status`, { status, cancellationReason });

export const deleteReservation = (id: string) =>
  axios.delete(`${BASE_API_URL}/reservations/${id}`);

export const addGuestReview = (
  id: string,
  data: { rating: number; comment?: string; waiterName: string }
) => axios.patch(`${BASE_API_URL}/reservations/${id}/review`, data);

export const getReservationSettings = (clientId: string) =>
  axios.get<ReservationSettings>(`${BASE_API_URL}/${clientId}/reservation-settings`);

export const saveReservationSettings = (clientId: string, data: Partial<ReservationSettings>) =>
  axios.put(`${BASE_API_URL}/${clientId}/reservation-settings`, data);

// ─── Public endpoints ─────────────────────────────────────────────────────────

export const getPublicAvailability = (clientId: string, date: string) =>
  axios.get<Availability>(`${BASE_API_URL}/public/reservations/availability`, {
    params: { clientId, date },
  });

export const createPublicReservation = (data: {
  clientId: string;
  guestName: string;
  guestPhone: string;
  guestEmail?: string;
  date: string;
  time: string;
  numberOfGuests: number;
  note?: string;
  source?: ReservationSource;
  voiceTranscript?: string;
}) => axios.post(`${BASE_API_URL}/public/reservations`, data);
